/**
 * FlowchartsIndexPage — `/flowcharts`.
 *
 * Browse the curated catalogue of UPMP MVP feature flowcharts. Mermaid
 * source is rendered as a `<pre>` block on each detail page — the runtime
 * is not bundled in web-staff to keep the build lean.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Layers, Workflow } from 'lucide-react';

import { PageHeader } from '../shared/StaffUI';
import { FLOWCHARTS, MODULES, groupByModule } from './flowchartRegistry';

interface StatTileProps {
  readonly icon: typeof Workflow;
  readonly label: string;
  readonly value: string | number;
  readonly tint: string;
}

function StatTile({ icon: Icon, label, value, tint }: StatTileProps): JSX.Element {
  return (
    <div className="bg-white border border-border rounded-xl p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tint}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-lg font-bold text-gray-900 leading-none">{value}</p>
        <p className="text-[10px] text-muted uppercase tracking-wider mt-1">{label}</p>
      </div>
    </div>
  );
}

export function FlowchartsIndexPage(): JSX.Element {
  const grouped = groupByModule();
  const total = FLOWCHARTS.length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="UPMP feature & process flowcharts"
        description="One diagram per MVP feature and one per cross-cutting process."
      />

      <div className="grid grid-cols-3 gap-4">
        <StatTile icon={Workflow} label="Diagrams" value={total} tint="bg-nipost-green/10 text-nipost-green" />
        <StatTile icon={Layers} label="Sections" value={MODULES.length} tint="bg-blue-50 text-blue-600" />
        <StatTile icon={FileText} label="MVP scope" value="Phase 1" tint="bg-amber-50 text-amber-700" />
      </div>

      <div className="space-y-6">
        {MODULES.map((m) => {
          const section = grouped[m.slug];
          if (!section || section.items.length === 0) return null;
          return (
            <section key={m.slug}>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-lg font-bold text-gray-900">{m.label}</h2>
                <span className="text-xs text-muted">{section.items.length} diagrams</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.items.map((fc) => (
                  <Link
                    key={fc.id}
                    to={`/flowcharts/${fc.id}`}
                    className="group flex items-start justify-between gap-3 p-4 bg-white border border-border rounded-xl hover:border-nipost-green hover:shadow-sm transition-all"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 group-hover:text-nipost-green">
                        {fc.title}
                      </p>
                      {fc.featureIds.length > 0 ? (
                        <p className="text-[10px] text-muted font-mono mt-1 truncate">
                          {fc.featureIds.join(' · ')}
                        </p>
                      ) : null}
                      <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">{fc.description}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted group-hover:text-nipost-green flex-shrink-0 mt-0.5" />
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
